import type { Cluster, Question } from "../../../lib/types";
import type { ScaleModel } from "../../../lib/stageViewModels";
import { CohortClusterColumns } from "./CohortClusterColumns";
import { useGrowIn } from "./useGrowIn";

export function ScaleDistribution({
  model,
  question,
  clusters,
}: {
  model: ScaleModel;
  question: Question;
  clusters: Cluster[];
}) {
  const { grown, reduced, delay } = useGrowIn();
  const isNps = question.question_type === "nps";
  const maxCount = Math.max(1, ...model.bins.map((b) => b.count));
  const columns = isNps
    ? [
        {
          cohort: "detractor",
          title: "Detraktoren sagen",
          count: model.bands.low,
          accent: false,
        },
        {
          cohort: "promoter",
          title: "Promotoren sagen",
          count: model.bands.high,
          accent: true,
        },
      ]
    : [
        {
          cohort: "low",
          title: "Kritische Stimmen (1–2)",
          count: model.bands.low,
          accent: false,
        },
        {
          cohort: "high",
          title: "Zustimmende Stimmen (4–5)",
          count: model.bands.high,
          accent: true,
        },
      ];

  return (
    <div className="flex flex-col gap-7 h-full text-rc-paper">
      <header className="flex-shrink-0 flex flex-col items-center text-center animate-fade-up">
        <span
          className="font-bold leading-none tabular-nums tracking-[-0.04em] text-rc-berry"
          style={{ fontSize: "clamp(4rem, 9vw, 8.5rem)" }}
        >
          {model.hero}
        </span>
        <div className="mt-2 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-rc-paper/55">
          <span>{model.heroCaption}</span>
          <span aria-hidden className="text-rc-paper/25">
            ·
          </span>
          <span className="tabular-nums">
            {model.total} {model.total === 1 ? "Antwort" : "Antworten"}
          </span>
        </div>
      </header>

      {model.total === 0 ? (
        <div className="flex-shrink-0 rounded-xl border border-dashed border-rc-paper/15 px-4 py-4 text-center text-[12px] text-rc-paper/40">
          Bewertungen erscheinen hier live.
        </div>
      ) : (
        <div className="flex-shrink-0 flex flex-col gap-2.5">
          <div className="flex items-end gap-2 h-28">
            {model.bins.map((bin, i) => (
              <div
                key={bin.value}
                className="flex-1 h-full flex flex-col items-center justify-end gap-1.5 min-w-0"
              >
                <span className="text-[12px] tabular-nums text-rc-paper/70">
                  {bin.count}
                </span>
                <div
                  className={`w-full rounded-t-md ${bin.count > 0 ? "bg-rc-berry" : "bg-rc-paper/10"} ${reduced ? "" : "transition-[height] duration-700 ease-out"}`}
                  style={{
                    height: grown
                      ? `${Math.max(4, Math.round((bin.count / maxCount) * 100))}%`
                      : "4%",
                    transitionDelay: delay(i),
                  }}
                  aria-label={`Wert ${bin.value}: ${bin.count}`}
                />
                <span className="text-[12px] font-semibold tabular-nums text-rc-paper/55">
                  {bin.value}
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[11px] tracking-wide text-rc-paper/40">
            <span>{model.minLabel}</span>
            <span>{model.maxLabel}</span>
          </div>
        </div>
      )}

      {clusters.length > 0 && (
        <CohortClusterColumns columns={columns} clusters={clusters} />
      )}
    </div>
  );
}
